import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../ui/use-toast';
import { Loader2, MessageSquare } from 'lucide-react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTenantContext } from '../../context/TenantContext';

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

interface CommentSectionProps {
  postId: string;
}

interface Comment {
  id: string;
  author_name: string;
  content: string;
  created_at: string;
  parent_id: string | null;
  replies?: Comment[];
}

// Le schéma de validation est robuste et est conservé
const commentSchema = z.object({
  author_name: z.string().min(2, { message: 'Le nom doit contenir au moins 2 caractères.' }),
  author_email: z.string().email({ message: 'Veuillez fournir une adresse email valide.' }),
  content: z.string().min(5, { message: 'Votre réflexion doit contenir au moins 5 caractères.' }),
});

type CommentFormValues = z.infer<typeof commentSchema>;

export const CommentSection = ({ postId }: CommentSectionProps) => {
  const { toast } = useToast();
  const { tenant } = useTenantContext();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [replyTo, setReplyTo] = useState<string | null>(null);

  const form = useForm<CommentFormValues>({
    resolver: zodResolver(commentSchema),
    defaultValues: { author_name: '', author_email: '', content: '' },
  });

  // La logique de récupération et de tri des commentaires est excellente et conservée
  const fetchComments = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('blog_comments')
        .select('*')
        .eq('post_id', postId)
        .eq('status', 'approved')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const parentComments: Comment[] = [];
      const replyMap = new Map<string, Comment[]>();

      data?.forEach(comment => {
        if (comment.parent_id) {
          if (!replyMap.has(comment.parent_id)) {
            replyMap.set(comment.parent_id, []);
          }
          replyMap.get(comment.parent_id)!.push(comment);
        } else {
          parentComments.push(comment);
        }
      });
      
      parentComments.forEach(p => {
        p.replies = replyMap.get(p.id) || [];
      });

      setComments(parentComments);
    } catch (error) {
      console.error('Erreur lors de la récupération des commentaires:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (postId) {
      fetchComments();
    }
  }, [postId]);

  const onSubmit = async (values: CommentFormValues) => {
    setSubmitting(true);
    try {
      const { error } = await supabase.from('blog_comments').insert({
        post_id: postId,
        author_name: values.author_name,
        author_email: values.author_email,
        content: values.content,
        parent_id: replyTo,
      });

      if (error) throw error;

      toast({
        title: 'Réflexion partagée',
        description: 'Votre contribution a été envoyée et sera publiée après modération.',
      });

      form.reset();
      setReplyTo(null);
    } catch (error) {
      console.error('Erreur lors de l\'envoi du commentaire:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Impossible de partager votre réflexion. Veuillez réessayer.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = (commentId: string) => {
    setReplyTo(commentId);
    document.getElementById('comment-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const cancelReply = () => setReplyTo(null);

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 flex items-center" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
        <MessageSquare className="mr-3" size={24} style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
        Échanges & Réflexions
      </h2>

      {loading ? (
        <div className="flex justify-center items-center py-8">
          <div className="relative">
            <div className="w-8 h-8 border-4 rounded-full animate-spin"
              style={{
                borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2),
                borderTopColor: tenant?.config?.color1 || '#fe8d17'
              }}
            ></div>
            <div className="absolute inset-0 w-8 h-8 border-4 border-transparent rounded-full animate-spin" 
              style={{ 
                borderTopColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.3),
                animationDirection: 'reverse',
                animationDuration: '1.5s'
              }}
            ></div>
          </div>
          <span className="ml-3" style={{ color: tenant?.config?.color4 || '#9ca3af' }}>Chargement des échanges...</span>
        </div>
      ) : comments.length === 0 ? (
        <div className="text-center py-8 rounded-lg">
          <p style={{ color: tenant?.config?.color4 || '#9ca3af' }}>Soyez le premier à partager une réflexion sur cet article.</p>
        </div>
      ) : (
        <div className="space-y-6 mb-10">
          {comments.map((comment) => (
            <div key={comment.id} className="p-4 rounded-lg border backdrop-blur-sm"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderColor: `${tenant?.config?.color3 || '#fe8d17'}4d`
              }}
            >
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{comment.author_name}</span>
                <span className="text-sm" style={{ color: tenant?.config?.color4 || '#9ca3af' }}>
                  {format(new Date(comment.created_at), 'dd MMMM yyyy', { locale: fr })}
                </span>
              </div>
              <p className="mb-3" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{comment.content}</p>
              <Button
                variant="link"
                size="sm"
                className="p-0 h-auto text-sm font-semibold transition-colors"
                style={{ color: tenant?.config?.color1 || '#fe8d17' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = tenant?.config?.color2 || '#008aef';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
                }}
                onClick={() => handleReply(comment.id)}
              >
                Répondre
              </Button>

              {comment.replies && comment.replies.length > 0 && (
                <div className="mt-4 ml-4 pl-4 border-l-2 space-y-4" style={{ borderColor: hexToRgba(tenant?.config?.color3 || '#fe8d17', 0.5) }}>
                  {comment.replies.map((reply) => (
                    <div key={reply.id} className="p-3 rounded-lg border backdrop-blur-sm"
                      style={{
                        backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1),
                        borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2)
                      }}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{reply.author_name}</span>
                        <span className="text-sm" style={{ color: tenant?.config?.color4 || '#9ca3af' }}>
                          {format(new Date(reply.created_at), 'dd MMMM yyyy', { locale: fr })}
                        </span>
                      </div>
                      <p style={{ color: tenant?.config?.color3 || '#ffffff' }}>{reply.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div id="comment-form" className="mt-10 pt-6 border-t" style={{ borderColor: hexToRgba(tenant?.config?.color3 || '#fe8d17', 0.2) }}>
        <h3 className="text-xl font-bold mb-4" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
          {replyTo ? 'Répondre à une réflexion' : 'Partager votre réflexion'}
        </h3>
        
        {replyTo && (
          <div className="mb-4 p-3 rounded-md flex justify-between items-center border backdrop-blur-sm"
            style={{
              backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1),
              borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.3)
            }}
          >
            <p className="text-sm" style={{ color: tenant?.config?.color4 || '#9ca3af' }}>Vous répondez à un commentaire.</p>
            <Button variant="link" size="sm" onClick={cancelReply} className="p-0 h-auto transition-colors"
              style={{ color: tenant?.config?.color1 || '#fe8d17' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = tenant?.config?.color2 || '#008aef';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
              }}
            >
              Annuler
            </Button>
          </div>
        )}

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="author_name" style={{ color: tenant?.config?.color4 || '#9ca3af' }}>Nom *</Label>
              <Input 
                id="author_name" 
                {...form.register('author_name')} 
                placeholder="Votre nom" 
                className="bg-white/10 border-white/20 placeholder:text-white/60 transition-all duration-300"
                style={{
                  color: tenant?.config?.color3 || '#ffffff',
                  '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                } as React.CSSProperties}
              />
              {form.formState.errors.author_name && <p className="text-sm text-red-500">{form.formState.errors.author_name.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="author_email" style={{ color: tenant?.config?.color4 || '#9ca3af' }}>
                Email * <span className="text-xs" style={{ color: hexToRgba(tenant?.config?.color4 || '#9ca3af', 0.7) }}>(ne sera pas publié)</span>
              </Label>
              <Input 
                id="author_email" 
                type="email" 
                {...form.register('author_email')} 
                placeholder="Votre adresse email" 
                className="bg-white/10 border-white/20 placeholder:text-white/60 transition-all duration-300"
                style={{
                  color: tenant?.config?.color3 || '#ffffff',
                  '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                } as React.CSSProperties}
              />
              {form.formState.errors.author_email && <p className="text-sm text-red-500">{form.formState.errors.author_email.message}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="content" style={{ color: tenant?.config?.color4 || '#9ca3af' }}>Votre réflexion *</Label>
            <Textarea 
              id="content" 
              {...form.register('content')} 
              placeholder="Écrivez votre message ici..." 
              className="min-h-32 bg-white/10 border-white/20 placeholder:text-white/60 transition-all duration-300"
              style={{
                color: tenant?.config?.color3 || '#ffffff',
                '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
              } as React.CSSProperties}
            />
            {form.formState.errors.content && <p className="text-sm text-red-500">{form.formState.errors.content.message}</p>}
          </div>

          <div className="flex items-center justify-end">
            <Button 
              type="submit" 
              disabled={submitting} 
              className="shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
              style={{
                backgroundColor: tenant?.config?.color1 || '#fe8d17',
                color: tenant?.config?.color3 || '#ffffff',
                backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                boxShadow: `0 4px 15px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4)}`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
              }}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                'Partager'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CommentSection;